import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../config';
import {
  Zap, ArrowLeft, Flame, Snowflake, Filter, Calendar,
  BarChart3, TrendingUp, TrendingDown, Minus, Clock, AlertTriangle
} from 'lucide-react';

/**
 * AnimalitosView — Módulo de análisis de números calientes y fríos.
 * 
 * Vista 1: Grid de cards con cada lotería ANIMALITO, mostrando nombre,
 *           cobertura real de datos, y horas disponibles.
 * Vista 2: Click en card → tablero con calientes (🔴/llama) y fríos (❄️/copo),
 *           con universo dinámico por lotería.
 * 
 * Filtros: rango de fechas (default 01-01-2026 → hoy), hora de sorteo.
 * Datos:   100% READ-ONLY desde turflottery vía backend kronologic.
 */
export default function AnimalitosView() {
  // ── Estado ────────────────────────────────────────────────────────
  const [loterias, setLoterias] = useState({ conDatos: [], sinDatos: [] });
  const [loteriaSel, setLoteriaSel] = useState(null);    // card clickeada
  const [analisis, setAnalisis] = useState(null);          // resultado del engine
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filtros
  const [desde, setDesde] = useState('2026-01-01');
  const [hasta, setHasta] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [horaSel, setHoraSel] = useState(null);

  // ── Carga loterías ────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API_URL}/api/animalitos/loterias`)
      .then(r => setLoterias(r.data))
      .catch(e => setError('Error cargando loterías: ' + (e.response?.data?.detail || e.message)));
  }, []);

  // ── Carga análisis ────────────────────────────────────────────────
  const cargarAnalisis = useCallback(async (codigo) => {
    setLoading(true);
    setError(null);
    try {
      const params = { codigo, desde, hasta };
      if (horaSel) params.hora = horaSel;
      const r = await axios.get(`${API_URL}/api/animalitos/analisis`, { params });
      setAnalisis(r.data);
    } catch (e) {
      setError('Error en análisis: ' + (e.response?.data?.detail || e.response?.data?.message || e.message));
      setAnalisis(null);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, horaSel]);

  // Click en card
  const seleccionarLoteria = (lot) => {
    setLoteriaSel(lot);
    setAnalisis(null);
    setHoraSel(null);
    cargarAnalisis(lot.codigo);
  };

  // Recargar análisis al cambiar filtros (si hay lotería seleccionada)
  useEffect(() => {
    if (loteriaSel) {
      cargarAnalisis(loteriaSel.codigo);
    }
    // eslint-disable-next-line
  }, [desde, hasta, horaSel]);

  // ── Vista detalle ─────────────────────────────────────────────────
  if (loteriaSel) {
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <button
            onClick={() => { setLoteriaSel(null); setAnalisis(null); }}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} /> Volver a loterías
          </button>

          <h2 className="text-xl font-black text-white uppercase tracking-tight">
            {loteriaSel.nombre}
            <span className="ml-2 text-xs font-bold text-yellow-500 bg-yellow-500/10 px-2 py-0.5 rounded">
              {loteriaSel.tipo}
            </span>
          </h2>
        </div>

        {/* Filtros */}
        <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2 text-yellow-500">
            <Filter size={16} />
            <span className="text-xs font-bold uppercase tracking-widest">Filtros</span>
          </div>

          <div>
            <label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Desde</label>
            <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
              className="bg-slate-950 border border-slate-700 text-white text-sm px-3 py-1.5 rounded-lg" />
          </div>

          <div>
            <label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Hasta</label>
            <input type="date" value={hasta} onChange={e => setHasta(e.target.value)}
              className="bg-slate-950 border border-slate-700 text-white text-sm px-3 py-1.5 rounded-lg" />
          </div>

          <div>
            <label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Hora</label>
            <select value={horaSel || ''} onChange={e => setHoraSel(e.target.value || null)}
              className="bg-slate-950 border border-slate-700 text-white text-sm px-3 py-1.5 rounded-lg">
              <option value="">Todas las horas</option>
              {(loteriaSel.horasDisponibles || []).map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500"></div>
            <span className="ml-3 text-slate-400 font-bold">Calculando tendencias...</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-2xl text-red-400 text-sm">
            <AlertTriangle size={16} className="inline mr-2" /> {error}
          </div>
        )}

        {/* Resultado */}
        {!loading && analisis && (
          <AnalisisDetalle data={analisis} loteria={loteriaSel} />
        )}

        {/* Sin datos */}
        {!loading && analisis && analisis.totalSorteos === 0 && !error && (
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl text-center">
            <Clock size={40} className="text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 font-bold">Sin datos para este rango de fechas.</p>
            <p className="text-slate-600 text-sm mt-2">
              La lotería {loteriaSel.nombre} no tiene resultados en la base de turflottery
              para el período {desde} → {hasta}. El scraper de turf-lottery-clean los generará
              cuando se ejecute.
            </p>
          </div>
        )}
      </div>
    );
  }

  // ── Vista grid de cards ───────────────────────────────────────────
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-black text-white uppercase tracking-tight">
            Loterías de Animalitos
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Datos en vivo desde turflottery — análisis calientes y fríos
          </p>
        </div>

        {/* Filtros globales */}
        <div className="flex items-end gap-3 bg-slate-900 border border-slate-800 px-4 py-3 rounded-2xl">
          <Calendar size={16} className="text-yellow-500 mb-0.5" />
          <div>
            <label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Desde</label>
            <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
              className="bg-slate-950 border border-slate-700 text-white text-xs px-2 py-1 rounded-lg" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Hasta</label>
            <input type="date" value={hasta} onChange={e => setHasta(e.target.value)}
              className="bg-slate-950 border border-slate-700 text-white text-xs px-2 py-1 rounded-lg" />
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-2xl text-red-400 text-sm">
          <AlertTriangle size={16} className="inline mr-2" /> {error}
        </div>
      )}

      {/* Cards con datos */}
      {loterias.conDatos?.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-yellow-500 uppercase tracking-widest mb-3">
            Con datos ({loterias.conDatos.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {loterias.conDatos.map(lot => (
              <LoteriaCard key={lot.codigo} lot={lot} onClick={() => seleccionarLoteria(lot)} />
            ))}
          </div>
        </div>
      )}

      {/* Cards sin datos */}
      {loterias.sinDatos?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-bold text-slate-600 uppercase tracking-widest mb-3">
            Sin datos aún ({loterias.sinDatos.length})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {loterias.sinDatos.map(lot => (
              <div key={lot.codigo}
                className="bg-slate-900/50 border border-slate-800/50 p-4 rounded-2xl opacity-50">
                <p className="text-slate-500 font-bold text-sm uppercase">{lot.nombre}</p>
                <p className="text-slate-600 text-[10px] mt-1">Sin resultados en turflottery</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {!loterias.conDatos && !loterias.sinDatos && !error && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500"></div>
        </div>
      )}
    </div>
  );
}

// ── Sub-componente: Card de lotería ─────────────────────────────────

function LoteriaCard({ lot, onClick }) {
  return (
    <button onClick={onClick}
      className="bg-slate-900 border border-slate-800 p-5 rounded-2xl text-left
                 hover:border-yellow-500/50 hover:shadow-[0_0_20px_rgba(234,179,8,0.1)]
                 transition-all duration-200 group relative overflow-hidden flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="bg-yellow-500/10 w-10 h-10 rounded-xl flex items-center justify-center text-yellow-500 group-hover:bg-yellow-500 group-hover:text-slate-900 transition-all">
          <Zap size={20} />
        </div>
        <div>
          <h3 className="text-white font-black text-sm uppercase tracking-tight group-hover:text-yellow-500 transition-colors">
            {lot.nombre}
          </h3>
          <p className="text-slate-500 text-[10px] mt-0.5 font-bold uppercase tracking-wider">{lot.tipo || 'ANIMALITO'}</p>
        </div>
      </div>
    </button>
  );
}

// ── Sub-componente: Detalle del análisis ─────────────────────────────

function AnalisisDetalle({ data, loteria }) {
  const [tab, setTab] = useState('calientes');

  return (
    <div className="space-y-6">
      {/* Resumen del bucket */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Sorteos" value={data.totalSorteos} color="text-white" />
        <StatBox label="Espacio" value={`${data.espacio} números`} color="text-slate-300" />
        <StatBox label="Esperado" value={data.esperadoPorNumero} color="text-blue-400" />
        <StatBox
          label="Chi²"
          value={
            data.uniforme
              ? `✓ Uniforme (${data.chiCuadrado})`
              : `⚠ Sesgo (${data.chiCuadrado})`
          }
          color={data.uniforme ? 'text-green-400' : 'text-orange-400'}
        />
      </div>

      {/* Tabs Calientes / Fríos */}
      <div className="flex gap-2">
        <button onClick={() => setTab('calientes')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all ${
            tab === 'calientes'
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-slate-900 text-slate-500 border border-slate-800 hover:text-red-400'
          }`}>
          <Flame size={16} /> Calientes ({data.calientes.length})
        </button>
        <button onClick={() => setTab('frios')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all ${
            tab === 'frios'
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : 'bg-slate-900 text-slate-500 border border-slate-800 hover:text-blue-400'
          }`}>
          <Snowflake size={16} /> Fríos ({data.frios.length})
        </button>
      </div>

      {/* Grid de números */}
      {tab === 'calientes' && (
        <div>
          <h3 className="text-xs font-bold text-red-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <Flame size={14} /> Números Calientes
          </h3>
          {data.calientes.length === 0 ? (
            <p className="text-slate-500 text-sm">No hay números calientes en este período.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {data.calientes.slice(0, 6).map(m => (
                <NumeroCard key={m.numero} m={m} variant="caliente" />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'frios' && (
        <div>
          <h3 className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <Snowflake size={14} /> Números Fríos
          </h3>
          {data.frios.length === 0 ? (
            <p className="text-slate-500 text-sm">No hay números fríos en este período.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {data.frios.slice(0, 10).map(m => (
                <NumeroCard key={m.numero} m={m} variant="frio" />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Frases resumen */}
      {data.frases?.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl space-y-1">
          {data.frases.map((f, i) => (
            <p key={i} className="text-slate-400 text-xs leading-relaxed">{f}</p>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sub-componente: StatBox ──────────────────────────────────────────

function StatBox({ label, value, color }) {
  return (
    <div className="bg-slate-900 border border-slate-800 p-3 rounded-2xl">
      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{label}</p>
      <p className={`text-sm font-black mt-1 ${color}`}>{value}</p>
    </div>
  );
}

// ── Sub-componente: NumeroCard (caliente/frío) ──────────────────────

function NumeroCard({ m, variant }) {
  const isCaliente = variant === 'caliente';
  const bg = isCaliente ? 'bg-red-500/10 border-red-500/20' : 'bg-blue-500/10 border-blue-500/20';
  const textMain = isCaliente ? 'text-red-400' : 'text-blue-400';

  const TrendIcon = m.tendencia === 'SUBIENDO' ? TrendingUp
    : m.tendencia === 'BAJANDO' ? TrendingDown : Minus;
  const trendColor = m.tendencia === 'SUBIENDO' ? 'text-green-400'
    : m.tendencia === 'BAJANDO' ? 'text-red-400' : 'text-slate-500';

  return (
    <div className={`${bg} border p-4 rounded-2xl relative overflow-hidden`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-4xl font-black ${textMain}`}>{m.numero}</p>
          {m.etiqueta && (
            <p className="text-sm text-white font-bold uppercase mt-1">{m.etiqueta}</p>
          )}
        </div>
        <TrendIcon size={16} className={trendColor} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div>
          <p className="text-white font-bold">Frecuencia</p>
          <p className="font-black text-white">{m.frecuencia} <span className="font-normal text-white">({m.porcentaje}%)</span></p>
        </div>
        <div>
          <p className="text-white font-bold">Atraso</p>
          <p className="font-black text-white">{m.atraso} <span className="font-normal text-white">sorteos</span></p>
        </div>
        <div>
          <p className="text-white font-bold">Desvío</p>
          <p className="font-black text-white">{m.desviacion > 0 ? '+' : ''}{m.desviacion}</p>
        </div>
        <div>
          <p className="text-white font-bold">Racha</p>
          <p className="font-black text-white">{m.rachaActual}/{m.rachaMaxima}</p>
        </div>
      </div>
    </div>
  );
}
